<template>
  <div>
    <div class="flex items-center justify-center mb-6">
      <MenuDropDown ref="boardModeSwitcher"
                    :title="currentBoardMode.label"
                    button-classes="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 border border-blue-700 rounded">
        <li v-for="mode in boardModes" v-if="currentModeValue !== mode.value">
          <a href="javascript:;"
             class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
             v-text="mode.label"
             @click.prevent="switchBoardMode(mode)">
          </a>
        </li>
      </MenuDropDown>

      <MenuDropDown ref="boardRangeSwitcher"
                    :title="currentBoardRange.label"
                    button-classes="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 border border-blue-700 rounded">
        <li v-for="range in boardRanges" v-if="currentRangeValue !== range.value">
          <a href="javascript:;"
             class="px-4 py-2 block text-gray-900 hover:bg-indigo-400 hover:text-white no-underline hover:no-underline"
             v-text="range.label"
             @click.prevent="switchBoardRange(range)">
          </a>
        </li>
      </MenuDropDown>
    </div>

    <div v-if="!loading && !hasBoardItems" class="text-center">
      <p v-text="$t('messages.no_data_available')"></p>
    </div>

    <div class="divide-y divide-gray-400">
      <pulse-loader :loading="loading"
                    color="#000"
                    size="10px"
                    class="text-center mb-4">
      </pulse-loader>

      <div v-for="(item, index) in boardItems" class="flex items-center justify-between px-4 py-2">
        <label v-text="`${index + 1}. ${item.name}`"></label>
        <label v-text="item.score"></label>
      </div>
    </div>

  </div>
</template>

<script>
import {mapState} from 'vuex';
import format from 'date-fns/format';
import endOfDay from 'date-fns/endOfDay';
import endOfWeek from 'date-fns/endOfWeek';
import startOfDay from 'date-fns/startOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import MenuDropDown from '@/components/widgets/MenuDropDown.vue';
import {fetchLeaderBoardItems} from 'catchersScoringApi/leaderboards';

export default {
  components: {
    MenuDropDown,
    PulseLoader
  },

  data() {
    return {
      currentModeValue: 'catchers',
      currentRangeValue: 'today',
      loading: false,
      boardItems: []
    }
  },

  watch: {
    currentModeValue(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchBoardData();
      }
    },
    currentRangeValue(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchBoardData();
      }
    }
  },

  computed: {
    ...mapState('civium', [
      'experiment'
    ]),

    boardModes() {
      return [
        {
          label: this.$t('messages.leaderboard_mode_catchers_label'),
          value: 'catchers',
        },
        {
          label: this.$t('messages.leaderboard_mode_my_team_label'),
          value: 'my_team',
        },
        {
          label: this.$t('messages.leaderboard_mode_all_team_label'),
          value: 'all_teams',
        },
      ];
    },

    boardRanges() {
      return [
        {
          label: this.$t('messages.leaderboard_range_today_label'),
          value: 'today',
        },
        {
          label: this.$t('messages.leaderboard_range_this_week_label'),
          value: 'this_week',
        },
        {
          label: this.$t('messages.leaderboard_range_all_time_label'),
          value: 'all_time',
        },
      ];
    },

    /**
     * Board parameters.
     *
     * @return {URLSearchParams}
     */
    boardParameters() {
      let params = new URLSearchParams();

      params.append('mode', this.currentModeValue);

      if (this.currentRangeValue === 'today') {
        params.append('start_datetime', format(startOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'));
        params.append('end_datetime', format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'));
      }

      if (this.currentRangeValue === 'this_week') {
        params.append('start_datetime', format(startOfWeek(new Date()), 'yyyy-MM-dd HH:mm:ss'));
        params.append('end_datetime', format(endOfWeek(new Date()), 'yyyy-MM-dd HH:mm:ss'));
      }

      return params;
    },

    /**
     * Has items on the board.
     */
    hasBoardItems() {
      return this.boardItems.length > 0;
    },

    /**
     * Current selected baord mode.
     *
     * @return {Object}
     */
    currentBoardMode() {
      return this.boardModes.find(mode => mode.value === this.currentModeValue);
    },

    /**
     * Current selected baord mode.
     *
     * @return {Object}
     */
    currentBoardRange() {
      return this.boardRanges.find(range => range.value === this.currentRangeValue);
    }
  },

  methods: {
    /**
     * Switch board mode value.
     *
     * @param mode
     */
    switchBoardMode(mode) {
      this.currentModeValue = mode.value;
      this.$refs.boardModeSwitcher.closeManually();
    },

    /**
     * Switch board range value.
     *
     * @param range
     */
    switchBoardRange(range) {
      this.currentRangeValue = range.value;
      this.$refs.boardRangeSwitcher.closeManually();
    },

    /**
     * Fetch board data.
     */
    async fetchBoardData() {
      this.loading = true;

      let {data: {data}} = await fetchLeaderBoardItems({
        experiment: this.experiment.id,
        params: this.boardParameters
      });

      this.boardItems = data;

      this.loading = false;
    }
  },

  mounted() {
    this.fetchBoardData();
  }
}
</script>
